import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

const MaterialCategoryForm = ({ category, onSave, onCancel }) => {
  const { t, i18n } = useTranslation();
  const { isDark } = useTheme();
  const modalContentRef = useRef(null);
  const [formData, setFormData] = useState({
    name_en: category?.name_en || '',
    name_ar: category?.name_ar || ''
  });
  const [errors, setErrors] = useState({});

  // Prevent clicks outside modal content from closing the modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalContentRef.current && !modalContentRef.current.contains(event.target)) {
        // Prevent closing when clicking outside
        event.preventDefault();
        event.stopPropagation();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        // Prevent closing with Escape key
        event.preventDefault();
        event.stopPropagation();
      }
    };

    // Add event listeners
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name_en.trim()) {
      newErrors.name_en = t('validation.required');
    }
    
    if (!formData.name_ar.trim()) {
      newErrors.name_ar = t('validation.required');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Include category ID if editing
    const submitData = category ? { ...formData, id: category.id } : formData;
    onSave(submitData);
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="modal-content bg-card rounded-xl shadow-lg border border-border max-w-lg mx-auto p-6" ref={modalContentRef}>
        <div className={`flex justify-between items-center mb-6 ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}>
          <h2 className="text-xl font-semibold text-card-foreground">
            {category ? t('categories.editCategory') : t('categories.addNew')}
          </h2>
          <button
            onClick={onCancel}
            className="text-muted-foreground hover:text-card-foreground transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Category Name (EN) */}
          <div>
            <label className="block text-sm font-medium text-card-foreground mb-2">
              {t('categories.nameEn')} *
            </label>
            <input
              type="text"
              name="name_en"
              value={formData.name_en}
              onChange={handleChange}
              className={`w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring transition-colors bg-background text-foreground ${errors.name_en ? 'border-destructive' : ''}`}
              placeholder="e.g., Vegetables"
            />
            {errors.name_en && (
              <p className="text-destructive text-sm mt-2">{errors.name_en}</p>
            )}
          </div>

          {/* Category Name (AR) */}
          <div>
            <label className="block text-sm font-medium text-card-foreground mb-2">
              {t('categories.nameAr')} *
            </label>
            <input
              type="text"
              name="name_ar"
              value={formData.name_ar}
              onChange={handleChange}
              className={`w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring transition-colors bg-background text-foreground ${errors.name_ar ? 'border-destructive' : ''}`}
              placeholder="e.g., خضروات"
            />
            {errors.name_ar && (
              <p className="text-destructive text-sm mt-2">{errors.name_ar}</p>
            )}
          </div>

          {/* Form Actions */}
          <div className={`flex justify-end ${i18n.language === 'ar' ? 'space-x-reverse space-x-3' : 'space-x-3'} pt-6 border-t border-border`}>
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring transition-colors"
            >
              {t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MaterialCategoryForm; 